import { View } from 'react-native';

import { Avatar, Box, Heading, Text } from '@squire/ui';

import { MapPinFilledIcon } from '@/components/_icons/MapPinFilledIcon';
import { SquireLogoText } from '@/components/_icons/SquireLogoText';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { formatAddressToString } from '@/utils/addressUtils';

import { styles } from './styles';
import type { ShopBrandingHeaderProps } from './types';

const WORDMARK_WIDTH = 56;

const ShopBrandingHeader = ({
  shop,
  layout,
  headline,
  action,
  variant = 'default',
}: ShopBrandingHeaderProps) => {
  const { t } = useScreenTranslations('StandbyRegistrationScreen');
  const isDesktop = layout === 'desktop';
  // Figma's closed frame (15948:114224) only redraws the mobile column; the
  // desktop closed frame reuses the default branding layout.
  const isClosedMobile = variant === 'closed' && !isDesktop;

  const logoUrl = shop.logo?.url ?? shop.photos?.[0]?.url ?? undefined;
  const address = formatAddressToString({
    street: shop.address?.street,
    city: shop.address?.city,
    state: shop.address?.state,
    zip: shop.address?.zip,
  });

  const logo = (
    <Avatar
      uri={logoUrl}
      shape="square"
      size="lg"
      alt={shop.name}
      fallbackText={shop.name?.charAt(0)?.toUpperCase()}
      style={isClosedMobile ? styles.closedLogo : undefined}
    />
  );

  const poweredBy = (
    <Box style={styles.poweredByLockup} testID="standby-powered-by">
      <Text variant="xs" color="textTertiary">
        {t('branding.poweredBy')}
      </Text>
      <SquireLogoText size={WORDMARK_WIDTH} color="iconTertiary" />
    </Box>
  );

  const nameBlock = (
    <Box style={styles.nameBlock}>
      <Box>
        <Heading
          variant={isDesktop ? '3xl' : 'l'}
          color="textPrimary"
          accessibilityRole="header"
        >
          {headline ?? t('branding.headline')}
        </Heading>
        <Heading
          variant={isDesktop ? '3xl' : 'l'}
          color="textPrimary"
          accessibilityRole="header"
        >
          {shop.name}
        </Heading>
      </Box>
      {address ? (
        <Box style={styles.addressRow}>
          <MapPinFilledIcon size={14} color="iconSecondary" />
          <Text
            variant={isDesktop ? 'l' : 'm'}
            color="textSecondary"
            style={styles.address}
          >
            {address}
          </Text>
        </Box>
      ) : null}
    </Box>
  );

  if (isDesktop) {
    // Name/address + CTA are one cluster so space-between keeps the logo top and powered-by bottom.
    const middle = action ? (
      <Box style={styles.desktopMiddle}>
        {nameBlock}
        {action}
      </Box>
    ) : (
      nameBlock
    );

    return (
      <Box style={styles.desktopColumn} testID="shop-branding-header-desktop">
        {logo}
        {middle}
        {poweredBy}
      </Box>
    );
  }

  return (
    <Box
      style={[styles.mobileColumn, isClosedMobile && styles.mobileColumnClosed]}
      testID="shop-branding-header-mobile"
    >
      <Box style={styles.mobileTopRow}>
        {logo}
        {poweredBy}
      </Box>
      {isClosedMobile ? (
        // Figma centers the name block vertically between the top row and the
        // pinned-bottom CTA (which lives in StandbyClosedScreen's mobileFooter).
        // Two equal empty flex spacers above/below do the centering: unistyles
        // drops `justifyContent` on a flex container on web (computed stays
        // 'normal'), but an empty `flex: 1` View still grows, so equal spacers
        // center the block reliably.
        <View
          style={styles.closedNameBlockRow}
          testID="shop-branding-header-closed-name-block"
        >
          <View
            style={styles.closedSpacer}
            testID="shop-branding-header-closed-spacer"
          />
          {nameBlock}
          <View
            style={styles.closedSpacer}
            testID="shop-branding-header-closed-spacer"
          />
        </View>
      ) : (
        nameBlock
      )}
      {action}
    </Box>
  );
};

ShopBrandingHeader.displayName = 'ShopBrandingHeader';
export { ShopBrandingHeader };
